import React from "react";

// Sentinel stored when a participant explicitly declines a sensitive item.
// Matches the Likert opt-out value so downstream analysis treats declines
// uniformly. Non-null, so a required item still passes validation once the
// participant has either answered or explicitly declined.
export const OPT_OUT = "prefer_not_to_answer";

const ScaleLabel = ({ label, fallback, align = "left" }) => {
  if (!label) return <span className="text-xs font-semibold text-spiders-text-secondary">{fallback}</span>;

  const mainPart = label.split("(")[0].replace(/^predominantly\s*/i, "").trim();
  const qualifier = label.match(/\(([^)]+)\)/)?.[1];

  return (
    <div className={align === "right" ? "text-right" : "text-left"}>
      <span className="text-xs font-semibold text-spiders-text-secondary block">{mainPart}</span>
      {qualifier && (
        <span className="text-[11px] text-spiders-text-muted leading-snug block mt-0.5">{qualifier}</span>
      )}
    </div>
  );
};

const ButtonScaleQuestion = ({ question, value, onChange }) => {
  const {
    questionId,
    min = 1,
    max = 7,
    step = 1,
    minLabel = "",
    maxLabel = "",
    centerLabel = "",
    hideValue = false,
    allowOptOut = false,
    optOutLabel = "I prefer not to answer",
  } = question;

  const points = [];
  for (let i = min; i <= max; i += step) {
    points.push(i);
  }

  const isLargeScale = points.length > 7;
  const buttonSize = isLargeScale ? "w-8 h-8 text-[10px]" : "w-10 h-10 text-xs";
  const centerIndex = Math.floor(points.length / 2);

  return (
    <div>
      <div className="flex gap-1.5 justify-center flex-wrap">
        {points.map((point, i) => (
          <button
            key={point}
            onClick={() => onChange(questionId, point)}
            className={`${buttonSize} rounded-full text-center transition-all font-medium flex items-center justify-center ${
              value === point
                ? "bg-spiders-primary-500 text-white ring-2 ring-spiders-primary-500/50 scale-110"
                : "bg-spiders-bg-elevated text-spiders-text-muted hover:bg-spiders-bg-light hover:text-spiders-text-secondary"
            }`}
            title={`${point}`}
          >
            {hideValue ? "" : point}
          </button>
        ))}
      </div>
      <div className="flex justify-between items-start mt-2 px-1 gap-4">
        <div className="flex-1 text-left">
          <ScaleLabel label={minLabel} fallback={min} />
        </div>
        {centerLabel && (
          <div className="flex-shrink-0 text-center">
            <span className="text-xs text-spiders-text-muted">{centerLabel}</span>
          </div>
        )}
        <div className="flex-1 text-right">
          <ScaleLabel label={maxLabel} fallback={max} align="right" />
        </div>
      </div>
      {allowOptOut && (
        <div className="mt-3 flex justify-center">
          <button
            onClick={() => onChange(questionId, value === OPT_OUT ? undefined : OPT_OUT)}
            className={`text-xs px-3 py-1.5 rounded-full transition-all ${
              value === OPT_OUT
                ? "bg-spiders-primary-500/20 text-spiders-primary-300 ring-1 ring-spiders-primary-500/50"
                : "text-spiders-text-muted hover:text-spiders-text-secondary"
            }`}
          >
            {optOutLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export default ButtonScaleQuestion;
